import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/auth/clerkShim';
import type { ImageBlock } from '@staty/shared/schemas/block';
import { BlockView } from '@staty/shared/components/BlockRenderer';
import { uploadImage, UploadError } from '../uploadImage';

/**
 * Image block: empty state is an upload target; once uploaded, the shared
 * BlockView renders exactly what the public page will. Alt/alignment/link
 * live in the settings panel; WIDTH is resizable two ways — the settings
 * slider, or the drag handle rendered here when the block is selected.
 *
 * The handle is positioned by MEASURING the img's corner (bottom-right; for
 * right-aligned images bottom-left, since their right edge is pinned) — a
 * CSS corner of the wrapper floats far from a small/centered image, which
 * made the handle undiscoverable. It re-measures on selection, value
 * changes, image load, drag, and window resize.
 */

interface Props {
  block: ImageBlock;
  selected: boolean;
  leagueName: string;
  onChange: (patch: Partial<ImageBlock>) => void;
}

const HANDLE_HALF = 9; // px — half the handle's hit box, centers it on the corner

export default function ImageBlockEditor({ block, selected, leagueName, onChange }: Props) {
  const { getToken } = useAuth();
  const fileInput = useRef<HTMLInputElement>(null);
  const wrapRef = useRef<HTMLDivElement>(null);
  const [uploading, setUploading] = useState(false);
  // Live width while dragging; null when idle (block value renders).
  const [dragPct, setDragPct] = useState<number | null>(null);
  const [handlePos, setHandlePos] = useState<{ left: number; top: number } | null>(null);

  const alignment = block.alignment || 'center';

  const measure = () => {
    const wrap = wrapRef.current;
    const img = wrap?.querySelector('img');
    if (!wrap || !img) {
      setHandlePos(null);
      return;
    }
    const wr = wrap.getBoundingClientRect();
    const ir = img.getBoundingClientRect();
    const cornerX = alignment === 'right' ? ir.left : ir.right;
    setHandlePos({
      left: cornerX - wr.left - HANDLE_HALF,
      top: ir.bottom - wr.top - HANDLE_HALF,
    });
  };

  // Track the image's corner: value changes re-measure synchronously; image
  // decode and window resizes re-measure asynchronously.
  useLayoutEffect(() => {
    if (selected) measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, block.widthPct, block.width, alignment, dragPct, block.src]);

  useEffect(() => {
    if (!selected) return undefined;
    const img = wrapRef.current?.querySelector('img');
    img?.addEventListener('load', measure);
    window.addEventListener('resize', measure);
    return () => {
      img?.removeEventListener('load', measure);
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, block.src]);

  const pick = () => fileInput.current?.click();

  const handleFile = async (file: File | undefined) => {
    if (!file || uploading) return;
    setUploading(true);
    try {
      const fileUrl = await uploadImage(file, leagueName, getToken);
      onChange({ src: fileUrl });
    } catch (err) {
      toast.error(err instanceof UploadError ? err.message : 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const startResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const wrap = wrapRef.current;
    const img = wrap?.querySelector('img');
    if (!wrap || !img) return;
    const box = wrap.getBoundingClientRect();
    const imgBox = img.getBoundingClientRect();
    // The anchor is the edge that stays put for this alignment; captured at
    // drag start so the math is stable while the image grows/shrinks.
    //   left-aligned:  left edge fixed  → width = pointer − left
    //   centered:      center fixed     → width = 2 × |pointer − center|
    //   right-aligned: right edge fixed → width = right − pointer
    const anchor =
      alignment === 'center'
        ? imgBox.left + imgBox.width / 2
        : alignment === 'right'
          ? imgBox.right
          : imgBox.left;
    let latest = block.widthPct ?? Math.round((imgBox.width / box.width) * 100);

    const onMove = (ev: PointerEvent) => {
      const px =
        alignment === 'center'
          ? Math.abs(ev.clientX - anchor) * 2
          : alignment === 'right'
            ? anchor - ev.clientX
            : ev.clientX - anchor;
      latest = Math.max(10, Math.min(100, Math.round((px / box.width) * 100)));
      setDragPct(latest);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      // The browser fires a CLICK at the release point; if the pointer ended
      // over a sibling/parent it would steal the selection and flip the
      // settings panel away from this image mid-resize. Swallow exactly one.
      window.addEventListener(
        'click',
        (ce) => {
          ce.stopPropagation();
          ce.preventDefault();
        },
        { capture: true, once: true }
      );
      setDragPct(null);
      onChange({ widthPct: latest });
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  if (!block.src) {
    return (
      <>
        <input
          ref={fileInput}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          type="button"
          className="canvas-block-empty canvas-block-empty--clickable"
          onClick={(e) => {
            e.stopPropagation();
            pick();
          }}
          disabled={uploading}
        >
          {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <ImagePlus className="h-5 w-5" />}
          <div className="canvas-block-empty__body">
            <p>{uploading ? 'Uploading…' : 'Upload an image'}</p>
            <p className="canvas-block-empty__hint">JPG/PNG up to 5MB, resized automatically</p>
          </div>
        </button>
      </>
    );
  }

  const renderBlock: ImageBlock =
    dragPct !== null ? { ...block, widthPct: dragPct } : block;

  return (
    <div ref={wrapRef} className="canvas-image" onDoubleClick={pick} title="Double-click to replace">
      <input
        ref={fileInput}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
      />
      <BlockView block={renderBlock} />
      {selected && handlePos && (
        <>
          <button
            type="button"
            className="canvas-image__resize"
            style={{ left: handlePos.left, top: handlePos.top }}
            aria-label="Drag to resize image"
            title="Drag to resize"
            onPointerDown={startResize}
          />
          {dragPct !== null && (
            <span
              className="canvas-image__pct"
              style={{ left: handlePos.left - 8, top: handlePos.top - 26 }}
            >
              {dragPct}%
            </span>
          )}
        </>
      )}
    </div>
  );
}
